import React, { useState } from 'react';

import {
  Box,
  Text,
  Input,
  FormControl,
  FormLabel,
  VStack,
  HStack,
  FormHelperText,
  Textarea,
} from '@chakra-ui/react';

import { useColors } from './colors';

const ListingDetails = ({ listing, onUpdate }) => {
  const [title, setTitle] = useState(listing?.details?.title || '');
  const [sku, setSku] = useState(listing?.sku || '');

  const {
    bgColor,
    borderColor,
    headingColor,
    subTextColor: labelColor,
    inputColor,
    placeholderColor,
    hoverBg: readOnlyBg,
  } = useColors();

  const readOnlyColor = inputColor;

  // Check if it's Amazon marketplace
  const isAmazon = listing?.marketplace?.name?.toLowerCase() === 'amazon';

  // Get character limit based on marketplace
  const getCharacterLimit = () => {
    return isAmazon ? 200 : 80;
  };

  const characterLimit = getCharacterLimit();
  const remainingChars = characterLimit - title.length;

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    if (newTitle.length <= characterLimit) {
      setTitle(newTitle);
      onUpdate?.({ title: newTitle });
    }
  };

  const handleSkuChange = (e) => {
    const newSku = e.target.value;
    setSku(newSku);
    onUpdate?.({ sku: newSku });
  };

  return (
    <Box
      bg={bgColor}
      borderRadius="xl"
      p={6}
      border="1px solid"
      borderColor={borderColor}
      flex="1"
      minH="100%"
      display="flex"
      flexDirection="column"
    >
      <Text fontSize="xl" fontWeight="semibold" color={headingColor} mb={6}>
        Listing Details
      </Text>

      <VStack spacing={5} align="stretch" flex="1">
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
            Title
          </FormLabel>

          {characterLimit === 200 ? (
            <Textarea
              value={title}
              onChange={handleTitleChange}
              placeholder="Enter product title..."
              size="md"
              maxLength={characterLimit}
              borderRadius="md"
              _focus={{
                borderColor: 'blue.400',
                boxShadow: '0 0 0 1px #3182ce',
              }}
              minRows={2}
              color={inputColor}
              _placeholder={{ color: placeholderColor }}
            />
          ) : (
            <Input
              value={title}
              onChange={handleTitleChange}
              placeholder="Enter product title..."
              size="md"
              maxLength={characterLimit}
              borderRadius="md"
              _focus={{
                borderColor: 'blue.400',
                boxShadow: '0 0 0 1px #3182ce',
              }}
              color={inputColor}
              _placeholder={{ color: placeholderColor }}
            />
          )}

          <FormHelperText
            fontSize="xs"
            color={remainingChars < 10 ? 'red.500' : 'gray.500'}
          >
            {`${remainingChars} / ${characterLimit}`}
          </FormHelperText>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
            SKU
          </FormLabel>
          <Input
            value={sku}
            onChange={handleSkuChange}
            placeholder="Enter SKU..."
            size="md"
            borderRadius="md"
            _focus={{
              borderColor: 'blue.400',
              boxShadow: '0 0 0 1px #3182ce',
            }}
            color={inputColor}
            _placeholder={{ color: placeholderColor }}
            isReadOnly={isAmazon}
            bg={isAmazon ? readOnlyBg : 'transparent'}
            cursor={isAmazon ? 'not-allowed' : 'text'}
          />
        </FormControl>

        <VStack spacing={4} align="stretch">
          <HStack spacing={4} align="flex-start">
            <FormControl flex="1">
              <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
                Brand
              </FormLabel>
              <Input
                value={listing?.marketplaceDetails?.brand || ''}
                isReadOnly
                bg={readOnlyBg}
                color={readOnlyColor}
                cursor="not-allowed"
                size="md"
                borderRadius="md"
                _placeholder={{ color: placeholderColor }}
              />
            </FormControl>

            {/* UPC Field - Read Only */}
            <FormControl flex="1">
              <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
                UPC
              </FormLabel>
              <Input
                value={listing?.marketplaceDetails?.upc || ''}
                isReadOnly
                bg={readOnlyBg}
                color={readOnlyColor}
                cursor="not-allowed"
                size="md"
                borderRadius="md"
              />
            </FormControl>
          </HStack>

          <HStack spacing={4} align="flex-start">
            {/* Warranty Field - Read Only */}
            <FormControl flex="1">
              <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
                Warranty
              </FormLabel>
              <Input
                value={listing?.marketplaceDetails?.warranty || ''}
                isReadOnly
                bg={readOnlyBg}
                color={readOnlyColor}
                cursor="not-allowed"
                size="md"
                borderRadius="md"
              />
            </FormControl>

            {/* EAN Field - Read Only - Only for Amazon */}
            {isAmazon && (
              <FormControl flex="1">
                <FormLabel fontSize="sm" fontWeight="semibold" color={labelColor} mb={2}>
                  EAN
                </FormLabel>
                <Input
                  value={listing?.marketplaceDetails?.ean || ''}
                  isReadOnly
                  bg={readOnlyBg}
                  color={readOnlyColor}
                  cursor="not-allowed"
                  size="md"
                  borderRadius="md"
                />
              </FormControl>
            )}
          </HStack>
        </VStack>
      </VStack>
    </Box>
  );
};

export default ListingDetails;
